import { Component, computed, effect, inject } from '@angular/core';
import { EAnalyticsStatus } from '@enums/campaign-analytics.enum';
import { IStreamApi } from '@interfaces/campaign-analytics-api.interface';
import { GraphUtilitiesService } from '@utilities/graph-utilities.service';
import { v4 as uuidv4 } from 'uuid';
import { DeliverableAnalyticsContentDirective } from '../../deliverable-analytics-content.directive';
import { ILineGraphInputData } from '../../graphs/line-graph/line-graph.interface';
import { StreamDetailsStore } from '../stream-details/stream-details.store';
import { AudioTrackingStreamAnalyticsComponent } from './deliverables/audio-tracking-stream-analytics/audio-tracking-stream-analytics.component';
import { ChatbotMultiStreamAnalyticsComponent } from './deliverables/chatbot-multi-stream-analytics/chatbot-multi-stream-analytics.component';
import { ChatbotStreamAnalyticsComponent } from './deliverables/chatbot-stream-analytics/chatbot-stream-analytics.component';
import { CustomStreamAnalyticsComponent } from './deliverables/custom-stream-analytics/custom-stream-analytics.component';
import { GamePlayStreamAnalyticsComponent } from './deliverables/game-play-stream-analytics/game-play-stream-analytics.component';
import { ProductPlacementStreamAnalyticsComponent } from './deliverables/product-placement-stream-analytics/product-placement-stream-analytics.component';
import { ShowcaseStreamAnalyticsComponent } from './deliverables/showcase-stream-analytics/showcase-stream-analytics.component';
import { TalkingPointAiStreamAnalyticsComponent } from './deliverables/talking-point-ai-stream-analytics/talking-point-ai-stream-analytics.component';
import { TalkingPointStreamAnalyticsComponent } from './deliverables/talking-point-stream-analytics/talking-point-stream-analytics.component';
import { TextTrackingStreamAnalyticsComponent } from './deliverables/text-tracking-stream-analytics/text-tracking-stream-analytics.component';
import { TitleStreamAnalyticsComponent } from './deliverables/title-stream-analytics/title-stream-analytics.component';
import { VisualStreamAnalyticsComponent } from './deliverables/visual-stream-analytics/visual-stream-analytics.component';

@Component({
  imports: [
    AudioTrackingStreamAnalyticsComponent,
    ChatbotMultiStreamAnalyticsComponent,
    ChatbotStreamAnalyticsComponent,
    GamePlayStreamAnalyticsComponent,
    ProductPlacementStreamAnalyticsComponent,
    ShowcaseStreamAnalyticsComponent,
    TalkingPointAiStreamAnalyticsComponent,
    TalkingPointStreamAnalyticsComponent,
    TextTrackingStreamAnalyticsComponent,
    TitleStreamAnalyticsComponent,
    VisualStreamAnalyticsComponent,
    CustomStreamAnalyticsComponent,
  ],
  providers: [StreamDetailsStore],
  selector: 'app-stream-details',
  styleUrls: ['./stream-details.component.scss'],
  templateUrl: './stream-details.component.html',
})
export class StreamDetailsComponent extends DeliverableAnalyticsContentDirective {
  public readonly preparedViewerGraphData = computed(() =>
    this.store.timeline$() ? this.prepareViewerGraphData() : [],
  );

  public readonly store = inject(StreamDetailsStore);
  private readonly graphUtilities = inject(GraphUtilitiesService);

  constructor() {
    super();

    effect(() => {
      if (!this.manualDeliverableStatusConfig().analyticsVisible) {
        this.store.patchState({ timeline: [] });

        return;
      }

      if (this.executionAnalytics()) {
        this.getTimelineData();
      }
    });
  }

  private getTimelineData(): void {
    const socialContentId = this.executionAnalytics()?.content?.socialContent?.uniqueExternalId;

    if (socialContentId) {
      this.store.fetchTimeline({ socialContentId });
    } else {
      this.store.patchState({ timeline: [] });
    }
  }

  private prepareViewerGraphData(): ILineGraphInputData[] {
    const timelineData = this.store.timeline$();

    if (!timelineData) {
      return [];
    }

    const stream = this.executionAnalytics()?.content?.socialContent as IStreamApi | null;
    const boundedPoints = this.graphUtilities.getBoundedViewerPoints(timelineData, stream?.executionTime, stream?.end);

    return boundedPoints.map((point) => ({
      id: uuidv4(),
      status: EAnalyticsStatus.ok,
      xAxisValue: point.timestamp,
      yAxisValue: point.viewCount,
    }));
  }
}
